// Klient zaplecza nowego układu: `/api/` (worker api) i `/brama/` (worker brama) pod adresem APP - na produkcji
// przekazuje je Worker APP (pwa/worker), lokalnie pośrednik Vite (docs/pwa/PROJEKT.md 2). Sesja = ciasteczko HttpOnly
// tego adresu; aplikacja ze sklepu - adres zaplecza i żeton z api/adres.ts (docs/pwa/NATYWNA.md 3), a bez internetu
// odpowiedzi ekranów z zakresu offline z telefonu (api/offline.ts).
import { useEffect, useState } from "react";
import type { KodBledu } from "../gen/kontrakt";
import { t } from "../i18n/t";
import { natywna } from "../uklad/instalacja";
import { adres, naglowki, ustawZeton } from "./adres";
import { odczytaj, odczytajJson, wZakresie, zapamietaj, zapamietajJson, zapomnij } from "./offline";

/** Strony sklepu (WordPress): dokumenty prawne i konto w sklepie (zakładanie konta, zamówienia i subskrypcja
 *  przepisów - „Zarządzaj”, WYGLAD.md #41) - jedno miejsce dla logowania, menu, Ustawień i Przepisów. */
export const SKLEP = {
  polityka: "https://termometrwifi.pl/polityka-prywatnosci/",
  regulamin: "https://termometrwifi.pl/regulamin-sklepu/",
  konto: "https://termometrwifi.pl/moje-konto/",
} as const;

/** Błąd zaplecza z kodem z kontraktu (`KodBledu`) albo `siec`, gdy odpowiedź w ogóle nie doszła. */
export class BladZaplecza extends Error {
  readonly kod: KodBledu | "siec";
  readonly status: number;
  /** Treść odpowiedzi (np. odrzucona komenda bramy niesie `wynik` z powodem). */
  readonly dane: unknown;
  constructor(kod: KodBledu | "siec", status: number, dane: unknown = null) {
    super(kod);
    this.kod = kod;
    this.status = status;
    this.dane = dane;
  }
}

type Metoda = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// Zalogowana APP (brama/polaczenie - uruchom): odmowa sesji w dowolnym zapytaniu kończy sesję w APP tak samo jak
// odrzucony strumień - bez tego po wygaśnięciu ciasteczka zostawały ekrany „Sesja wygasła” bez drogi do logowania.
let przyBrakuSesji: (() => void) | null = null;

export function ustawPrzyBrakuSesji(f: (() => void) | null): void {
  przyBrakuSesji = f;
}

function odmowa(kod: KodBledu | undefined, status: number, dane: unknown): BladZaplecza {
  if (kod === "brak_sesji") {
    void ustawZeton(""); // aplikacja ze sklepu: martwy żeton z Keychain / Keystore precz
    przyBrakuSesji?.();
  }
  return new BladZaplecza(kod ?? "chwilowa_awaria", status, dane);
}

/** Aplikacja ze sklepu bez zaplecza (sieć albo awaria serwera): odpowiedź zapisana do pracy offline (api/offline.ts)
 *  zamiast błędu; odmowa dostępu do tej ścieżki kasuje zapis. */
async function bezZaplecza<T>(sciezka: string, blad: BladZaplecza, odczyt: (k: string) => Promise<{ dane: T } | null>): Promise<T> {
  if (wZakresie(sciezka)) {
    if (blad.kod === "brak_dostepu" || blad.kod === "nie_ma" || blad.kod === "brak_sesji") void zapomnij(sciezka);
    else if (blad.kod === "siec" || blad.status >= 500) {
      const z = await odczyt(sciezka);
      if (z) return z.dane;
    }
  }
  throw blad;
}

export async function zapytanie<T>(metoda: Metoda, sciezka: string, tresc?: unknown): Promise<T> {
  // POST/PUT/PATCH zawsze z treścią JSON (bez danych - `{}`): zaplecze odrzuca inne zapisy (415, obrona przed CSRF).
  const zapis = metoda === "POST" || metoda === "PUT" || metoda === "PATCH";
  const offline = metoda === "GET" && wZakresie(sciezka);
  let odp: Response;
  try {
    odp = await fetch(adres(sciezka), {
      method: metoda,
      headers: await naglowki(zapis ? { "Content-Type": "application/json" } : undefined),
      body: zapis ? JSON.stringify(tresc ?? {}) : undefined,
    });
  } catch {
    const b = new BladZaplecza("siec", 0);
    return offline ? bezZaplecza(sciezka, b, odczytajJson<T>) : Promise.reject(b);
  }
  const dane = (await odp.json().catch(() => null)) as { ok?: boolean; error?: KodBledu } | null;
  if (!odp.ok || !dane || dane.ok === false) {
    const b = odmowa(dane?.error, odp.status, dane);
    return offline ? bezZaplecza(sciezka, b, odczytajJson<T>) : Promise.reject(b);
  }
  if (offline) void zapamietajJson(sciezka, dane);
  return dane as T;
}

/** Plik z zaplecza (PDF przepisu) jako bajty; odmowa - BladZaplecza z kodem i komunikatem z treści JSON. */
export async function plik(sciezka: string): Promise<ArrayBuffer> {
  const offline = wZakresie(sciezka);
  let odp: Response;
  try {
    odp = await fetch(adres(sciezka), { headers: await naglowki() });
  } catch {
    const b = new BladZaplecza("siec", 0);
    return offline ? bezZaplecza(sciezka, b, odczytaj) : Promise.reject(b);
  }
  if (!odp.ok) {
    const dane = (await odp.json().catch(() => null)) as { error?: KodBledu } | null;
    const b = odmowa(dane?.error, odp.status, dane);
    return offline ? bezZaplecza(sciezka, b, odczytaj) : Promise.reject(b);
  }
  const bajty = await odp.arrayBuffer();
  if (offline) void zapamietaj(sciezka, bajty.slice(0));
  return bajty;
}

/** Obrazek z zaplecza dla `<img>` (zdjęcia przepisów, wyrobów, wsadów, instrukcji). Przeglądarka - sam adres
 *  (ciasteczko); aplikacja ze sklepu - przez plik() z żetonem jako blob, zwalniany po odmontowaniu. Adresy spoza
 *  zaplecza (data:, blob:, https:) bez zmian. */
export function useObrazZaplecza(url: string | null | undefined): string | null | undefined {
  const zZaplecza = natywna() && !!url && url.startsWith("/");
  const [blob, ustawBlob] = useState<string | null>(null);
  useEffect(() => {
    if (!zZaplecza || !url) return;
    let zywy = true;
    let u = "";
    void plik(url).then((b) => {
      if (!zywy) return;
      u = URL.createObjectURL(new Blob([b]));
      ustawBlob(u);
    }).catch(() => undefined);
    return () => {
      zywy = false;
      if (u) URL.revokeObjectURL(u);
      ustawBlob(null);
    };
  }, [url, zZaplecza]);
  return zZaplecza ? blob : url;
}

/** Dokument HTML z zaplecza z obrazkami `src="/api/..."` (dokument instrukcji w ramce): w aplikacji ze sklepu obrazki
 *  podmienione na blob pobrany z żetonem (raz na adres - dokument otwierany ponownie bierze te same). */
const obrazyDokumentow = new Map<string, Promise<string>>();

export async function zObrazkamiZaplecza(html: string): Promise<string> {
  if (!natywna()) return html;
  const adresy = [...new Set([...html.matchAll(/src="(\/api\/[^"]+)"/g)].map((m) => m[1] ?? ""))].filter(Boolean);
  const bloby = await Promise.all(adresy.map((a) => {
    let p = obrazyDokumentow.get(a);
    if (!p) {
      p = plik(a.replaceAll("&amp;", "&")).then((b) => URL.createObjectURL(new Blob([b])));
      p.catch(() => obrazyDokumentow.delete(a));
      obrazyDokumentow.set(a, p);
    }
    return p.catch(() => a);
  }));
  return adresy.reduce((h, a, i) => h.replaceAll(`src="${a}"`, `src="${bloby[i] ?? a}"`), html);
}

/** Tekst dla człowieka do kodu błędu (kody z `auth/reply.go`). */
export function opisBledu(b: unknown): string {
  const kod = b instanceof BladZaplecza ? b.kod : "chwilowa_awaria";
  // Co poprawić, mówi serwer (`komunikat` przy kodach z opisem: taryfa, ustawienia, zaproszenie, serwis, urządzenie) -
  // ma pierwszeństwo przed ogólnym tekstem kodu.
  const komunikat = b instanceof BladZaplecza ? (b.dane as { komunikat?: string } | null)?.komunikat : undefined;
  if (komunikat) return t(komunikat);
  switch (kod) {
    case "siec":
      return t("Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.");
    case "zle_dane":
      return t("Nieprawidłowy login lub hasło.");
    case "za_duzo_prob":
      return t("Zbyt wiele nieudanych prób. Spróbuj ponownie za 15 minut.");
    case "niezweryfikowane":
      return t("Aby się zalogować, najpierw potwierdź swój adres e-mail.");
    case "przerwa_techniczna":
      return t("Trwa przerwa techniczna. Spróbuj ponownie za godzinę.");
    case "rejestracja_wylaczona":
      return t("Zakładanie nowych kont jest chwilowo wyłączone.");
    case "rejestracja_niedostepna":
      return t("Nie udało się teraz założyć konta. Spróbuj ponownie za chwilę.");
    case "zly_token_google":
      return t("Logowanie przez Google nie powiodło się. Spróbuj ponownie.");
    case "brak_google":
      return t("Logowanie przez Google jest chwilowo niedostępne.");
    case "zle_haslo":
      return t("Obecne hasło jest nieprawidłowe.");
    case "slabe_haslo":
      return t("Nowe hasło jest za krótkie.");
    case "zly_kod":
      return t("Link do zmiany hasła wygasł albo został już użyty. Poproś o nowy.");
    case "brak_sesji":
      return t("Sesja wygasła. Zaloguj się ponownie.");
    case "brak_dostepu":
      return t("Brak dostępu do tego urządzenia.");
    case "limit":
      return t("Osiągnięto limit na koncie - usuń coś, zanim dodasz nowe.");
    case "nie_ma":
      return t("Tego już nie ma - lista została odświeżona.");
    case "zla_taryfa":
      return t("Sprawdź godziny stref i ceny taryfy.");
    case "zle_ustawienia":
      return t("Sprawdź ustawienia i spróbuj ponownie.");
    case "zaproszenie":
      return t("Nie udało się obsłużyć zaproszenia. Spróbuj ponownie.");
    case "serwis":
      return t("Nie udało się obsłużyć prośby serwisu. Spróbuj ponownie.");
    case "urzadzenie":
      return t("Nie udało się obsłużyć urządzenia. Spróbuj ponownie.");
    case "rma":
      return t("Nie udało się zapisać zgłoszenia. Spróbuj ponownie.");
    case "taryfa_uzyta":
      return t("Ta taryfa rozliczyła już sesję energii - ustaw nową od innego dnia.");
    default:
      return t("Coś poszło nie tak po naszej stronie. Spróbuj ponownie za chwilę.");
  }
}
